import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HealingIcon from '@mui/icons-material/Healing';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { SoldierDTO, WarbandLevelUp, WarbandMemberDTO } from '../../types';
import { WarbandMemberCard } from '../shared/WarbandMemberCard';
import { WarbandMemberMiniCard } from '../shared/WarbandMemberMiniCard';
import { EmptyStateCard } from '../shared/EmptyStateCard';
import { ConfirmActionDialog } from '../shared/ConfirmActionDialog';
import { RemoveWizardModal } from '../modals/RemoveWizardModal';
import { ItemDetailsPopover } from '../shared/ItemDetailsPopover';
import { SectionHeading } from '../shared/SectionHeading';
import { useItemPopover, PopoverItem } from '../../hooks/useItemPopover';
import { formatSoldierType, computeUnusedLevelUps, computeLevelUpStatBonuses } from '../../utils/formatting';
import { groupSoldiersByType } from '../../utils/soldierGrouping';
import { gridFlexSx } from '../../App';
import { miniFlexSx } from '../../App';
import { createIceSurface } from '../../theme/surfaces';
import { manageWarbandService } from '../../services/manageWarbandService';

// ── Memoized per-soldier card ────────────────────────────────────────────────
// Defined outside ManageSoldiersTab so it is never recreated on parent renders.
interface ManageSoldierItemProps {
  soldier: SoldierDTO;
  onItemClick: (e: React.MouseEvent<HTMLElement>, item: PopoverItem) => void;
  onRemove: (soldier: SoldierDTO) => void;
  isDeleting: boolean;
  minified?: boolean;
}

const ManageSoldierItem = React.memo(function ManageSoldierItem({
  soldier,
  onItemClick,
  onRemove,
  isDeleting,
  minified,
}: ManageSoldierItemProps) {
  const updateMemberStoredInInn = useDirtyTrackingStore((s) => s.updateMemberStoredInInn);
  const toggleMemberBadlyWounded = useDirtyTrackingStore((s) => s.toggleMemberBadlyWounded);

  const titleMenuItems = useMemo(() => [
    {
      label: soldier.storedInInn !== 0 ? 'Remove from Inn' : 'Store in Inn',
      onClick: () => updateMemberStoredInInn(soldier.id, 'soldier', soldier.storedInInn !== 0 ? 0 : 1),
    },
    {
      label: soldier.badlyWounded ? 'Heal Bad Wound' : 'Mark as Badly Wounded',
      onClick: () => toggleMemberBadlyWounded(soldier.id, 'soldier'),
    },
    {
      label: 'Remove',
      onClick: () => onRemove(soldier),
      disabled: isDeleting,
    },
  ], [soldier, updateMemberStoredInInn, toggleMemberBadlyWounded, onRemove, isDeleting]);

  if (minified) {
    return (
      <WarbandMemberMiniCard
        member={soldier}
        isDefeated={soldier.currentHealth <= 0}
        showArmourWhenEmpty
        onItemClick={onItemClick}
        editMode
        titleMenuItems={titleMenuItems}
      />
    );
  }

  return (
    <WarbandMemberCard
      member={soldier}
      isDefeated={soldier.currentHealth <= 0}
      showArmourWhenEmpty
      onItemClick={onItemClick}
      editMode
      titleMenuItems={titleMenuItems}
    />
  );
});

export const ManageSoldiersTab: React.FC = () => {
  const selectedWarbandId = useWarbandStore((s) => s.selectedWarbandId);
  const warbands = useWarbandStore((s) => s.warbands);
  const soldiersData = useWarbandStore((s) => s.soldiersData);
  const wizardData = useWarbandStore((s) => s.wizardData);
  const apprenticeData = useWarbandStore((s) => s.apprenticeData);
  const openCreateWizardModal = useWarbandStore((s) => s.openCreateWizardModal);
  const openCreateApprenticeModal = useWarbandStore((s) => s.openCreateApprenticeModal);
  const levelUps = useWarbandStore((s) => s.levelUps);
  const setStoreLevelUps = useWarbandStore((s) => s.setLevelUps);
  const isMinified = useWarbandStore((s) => s.isMinified);

  const groupedSoldiers = useMemo(
    () => groupSoldiersByType(soldiersData?.soldiers.filter(s => s.storedInInn === 0 && !s.badlyWounded) || [], false),
    [soldiersData]
  );

  const innMembers = useMemo(() => {
    const members: WarbandMemberDTO[] = [];
    if (wizardData && wizardData.storedInInn !== 0) members.push(wizardData);
    if (apprenticeData && apprenticeData.storedInInn !== 0) members.push(apprenticeData);
    for (const soldier of soldiersData?.soldiers ?? []) {
      if (soldier.storedInInn !== 0) members.push(soldier);
    }
    return members;
  }, [wizardData, apprenticeData, soldiersData]);

  const badlyWoundedMembers = useMemo(() => {
    const members: WarbandMemberDTO[] = [];
    if (wizardData && wizardData.badlyWounded) members.push(wizardData);
    if (apprenticeData && apprenticeData.badlyWounded) members.push(apprenticeData);
    for (const soldier of soldiersData?.soldiers ?? []) {
      if (soldier.badlyWounded) members.push(soldier);
    }
    return members;
  }, [wizardData, apprenticeData, soldiersData]);

  const addSpellModalOpen = useWarbandStore((s) => s.ui.addSpellModalOpen);

  const warband = warbands.find((w) => w.id === selectedWarbandId);
  const unusedLevelUps = warband ? computeUnusedLevelUps(warband, levelUps) : 0;
  const levelUpBonuses = useMemo(() => computeLevelUpStatBonuses(levelUps), [levelUps]);

  const handleApplyLevelUp = async (stat: string) => {
    if (!selectedWarbandId || !warband) return;
    const totalSlots = Math.max(0, Math.floor(warband.experience / 100));
    const usedLevels = new Set(
      levelUps
        .filter((lu) => lu.statsIncrease || lu.spellImprovement || lu.spellLearned)
        .map((lu) => lu.level)
    );
    let targetLevel: number | null = null;
    for (let level = 2; level <= totalSlots + 1; level++) {
      if (!usedLevels.has(level)) {
        targetLevel = level;
        break;
      }
    }
    if (targetLevel === null) return;
    const newEntry: WarbandLevelUp = {
      warbandId: selectedWarbandId,
      level: targetLevel,
      statsIncrease: stat,
      spellImprovement: null,
      spellLearned: null,
    };
    const filledEntries = levelUps.filter((lu) => lu.statsIncrease || lu.spellImprovement || lu.spellLearned);
    const updatedLevelUps = [...filledEntries, newEntry];
    setStoreLevelUps(updatedLevelUps);
    try {
      await manageWarbandService.saveLevelUps(selectedWarbandId, {
        experience: warband.experience,
        levelUps: updatedLevelUps,
      });
    } catch (e) {
      setStoreLevelUps(levelUps);
      throw e;
    }
  };

  const {
    queueSoldierDelete,
    queueApprenticeDelete,
    queueWizardDelete,
    updateMemberStoredInInn,
    toggleMemberBadlyWounded,
  } = useDirtyTrackingStore();

  const { popover, handleItemClick, closePopover } = useItemPopover();

  const [deletingSoldierId, setDeletingSoldierId] = useState<string | null>(null);
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
  const [pendingRemoveSoldier, setPendingRemoveSoldier] = useState<SoldierDTO | null>(null);
  const [refundSoldierCost, setRefundSoldierCost] = useState(false);
  const [retainSoldierVaultItems, setRetainSoldierVaultItems] = useState(false);
  const [isRemoveApprenticeConfirmOpen, setIsRemoveApprenticeConfirmOpen] = useState(false);
  const [isDeletingApprentice, setIsDeletingApprentice] = useState(false);
  const [retainApprenticeVaultItems, setRetainApprenticeVaultItems] = useState(false);
  const [isRemoveWizardModalOpen, setIsRemoveWizardModalOpen] = useState(false);
  const [isDeletingWizard, setIsDeletingWizard] = useState(false);
  const [retainWizardVaultItems, setRetainWizardVaultItems] = useState(false);

  const handleRemoveSoldier = useCallback((soldier: SoldierDTO) => {
    if (!selectedWarbandId || deletingSoldierId) {
      return;
    }

    setPendingRemoveSoldier(soldier);
    setRefundSoldierCost(false);
    setRetainSoldierVaultItems(false);
    setIsRemoveConfirmOpen(true);
  }, [selectedWarbandId, deletingSoldierId]);

  const handleConfirmRemoveSoldier = async () => {
    if (!selectedWarbandId || !pendingRemoveSoldier || deletingSoldierId) {
      return;
    }

    setDeletingSoldierId(pendingRemoveSoldier.id);
    try {
      queueSoldierDelete(pendingRemoveSoldier.id, pendingRemoveSoldier.costPaid ?? pendingRemoveSoldier.cost, refundSoldierCost, retainSoldierVaultItems);
      setIsRemoveConfirmOpen(false);
      setPendingRemoveSoldier(null);
      setRefundSoldierCost(false);
      setRetainSoldierVaultItems(false);
    } finally {
      setDeletingSoldierId(null);
    }
  };

  const handleRemoveApprentice = useCallback(() => {
    if (!selectedWarbandId || isDeletingApprentice) return;
    setIsRemoveApprenticeConfirmOpen(true);
  }, [selectedWarbandId, isDeletingApprentice]);

  const handleRemoveWizard = useCallback(() => {
    if (!selectedWarbandId || isDeletingWizard) return;
    setIsRemoveWizardModalOpen(true);
  }, [selectedWarbandId, isDeletingWizard]);

  const handleConfirmRemoveWizard = (promote: boolean) => {
    setIsDeletingWizard(true);
    try {
      queueWizardDelete(promote, retainWizardVaultItems);
      setIsRemoveWizardModalOpen(false);
      setRetainWizardVaultItems(false);
    } finally {
      setIsDeletingWizard(false);
    }
  };

  const handleConfirmRemoveApprentice = () => {
    setIsDeletingApprentice(true);
    try {
      queueApprenticeDelete(retainApprenticeVaultItems);
      setIsRemoveApprenticeConfirmOpen(false);
      setRetainApprenticeVaultItems(false);
    } finally {
      setIsDeletingApprentice(false);
    }
  };

  const wizardMenuItems = useMemo(() => wizardData ? [
    { label: wizardData.storedInInn !== 0 ? 'Remove from Inn' : 'Store in Inn', onClick: () => updateMemberStoredInInn(wizardData.id, 'wizard', wizardData.storedInInn !== 0 ? 0 : 1) },
    { label: wizardData.badlyWounded ? 'Heal Bad Wound' : 'Mark as Badly Wounded', onClick: () => toggleMemberBadlyWounded(wizardData.id, 'wizard') },
    { label: 'Remove', onClick: handleRemoveWizard, disabled: isDeletingWizard },
  ] : [], [wizardData?.id, wizardData?.storedInInn, wizardData?.badlyWounded, updateMemberStoredInInn, toggleMemberBadlyWounded, handleRemoveWizard, isDeletingWizard]);

  const apprenticeMenuItems = useMemo(() => apprenticeData ? [
    { label: apprenticeData.storedInInn !== 0 ? 'Remove from Inn' : 'Store in Inn', onClick: () => updateMemberStoredInInn(apprenticeData.id, 'apprentice', apprenticeData.storedInInn !== 0 ? 0 : 1) },
    { label: apprenticeData.badlyWounded ? 'Heal Bad Wound' : 'Mark as Badly Wounded', onClick: () => toggleMemberBadlyWounded(apprenticeData.id, 'apprentice') },
    { label: 'Remove', onClick: handleRemoveApprentice, disabled: isDeletingApprentice },
  ] : [], [apprenticeData?.id, apprenticeData?.storedInInn, apprenticeData?.badlyWounded, updateMemberStoredInInn, toggleMemberBadlyWounded, handleRemoveApprentice, isDeletingApprentice]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Box sx={createIceSurface(11, { icicles: true, card: true })} p={1.5}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              {/* Wizard Section */}
              <SectionHeading label="Spellcasters" />
              <Box sx={isMinified ? miniFlexSx : gridFlexSx}>
                {wizardData && wizardData.storedInInn === 0 && !wizardData.badlyWounded ? (
                  <Box key={"wizard"}>
                    {wizardData && (
                      isMinified ? (
                        <WarbandMemberMiniCard
                          member={wizardData}
                          isDefeated={wizardData.currentHealth <= 0}
                          onItemClick={handleItemClick}
                          editMode={true}
                          unusedLevelUps={unusedLevelUps}
                          onApplyLevelUp={handleApplyLevelUp}
                          levelUpBonuses={levelUpBonuses}
                          titleMenuItems={wizardMenuItems}
                        />
                      ) : (
                        <WarbandMemberCard
                          member={wizardData}
                          isDefeated={wizardData.currentHealth <= 0}
                          onItemClick={handleItemClick}
                          editMode={true}
                          unusedLevelUps={unusedLevelUps}
                          onApplyLevelUp={handleApplyLevelUp}
                          levelUpBonuses={levelUpBonuses}
                          titleMenuItems={wizardMenuItems}
                        />
                      )
                    )}
                  </Box>
                ) : !wizardData ? (
                  <EmptyStateCard
                    message="No wizard assigned to this party"
                    actionLabel="Create Wizard"
                    actionIcon={<AddIcon />}
                    onAction={openCreateWizardModal}
                  />
                ) : null}
                {/* Apprentice Section */}
                <Box key={"apprentice"}>
                  {apprenticeData && apprenticeData.storedInInn === 0 && !apprenticeData.badlyWounded ? (
                    isMinified ? (
                      <WarbandMemberMiniCard
                        member={apprenticeData}
                        isDefeated={apprenticeData.currentHealth <= 0}
                        onItemClick={handleItemClick}
                        editMode={true}
                        levelUpBonuses={levelUpBonuses}
                        titleMenuItems={apprenticeMenuItems}
                      />
                    ) : (
                      <WarbandMemberCard
                        member={apprenticeData}
                        isDefeated={apprenticeData.currentHealth <= 0}
                        onItemClick={handleItemClick}
                        editMode={true}
                        levelUpBonuses={levelUpBonuses}
                        titleMenuItems={apprenticeMenuItems}
                      />
                    )
                  ) : !apprenticeData ? (
                    <EmptyStateCard
                      message="No apprentice assigned to this party"
                      actionLabel="Create Apprentice"
                      actionIcon={<AddIcon />}
                      onAction={openCreateApprenticeModal}
                      actionDisabled={!wizardData}
                      helperText={!wizardData ? 'Create a wizard first to enable apprentice creation.' : undefined}
                    />
                  ) : null}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Soldiers List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {groupedSoldiers.map((group, groupIdx) => (
          <Box key={group.type} sx={createIceSurface(groupIdx, { icicles: true, card: true })} p={1.5}>
            <Box>
              <SectionHeading label={formatSoldierType(group.type, true)} />
              <Box sx={isMinified ? miniFlexSx : gridFlexSx}>
                {group.members.map((soldier) => (
                  <ManageSoldierItem
                    key={soldier.id}
                    soldier={soldier}
                    onItemClick={handleItemClick}
                    onRemove={handleRemoveSoldier}
                    isDeleting={deletingSoldierId !== null}
                    minified={isMinified}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Badly Wounded */}
      {badlyWoundedMembers.length > 0 && (
        <Box sx={createIceSurface(43, { icicles: true, card: true })} p={1.5}>
          <SectionHeading label="Badly Wounded" />
          <Box sx={isMinified ? miniFlexSx : gridFlexSx}>
            {badlyWoundedMembers.map((member) => {
              const memberRole: 'wizard' | 'apprentice' | 'soldier' =
                member.creatureId.toLowerCase() === 'wizard' ? 'wizard'
                  : member.creatureId.toLowerCase() === 'apprentice' ? 'apprentice'
                    : 'soldier';
              return isMinified ? (
                <WarbandMemberMiniCard
                  key={member.id}
                  member={member}
                  isDefeated={true}
                  onItemClick={handleItemClick}
                  editMode={true}
                  titleMenuItems={[
                    {
                      label: 'Heal Bad Wound',
                      onClick: () => toggleMemberBadlyWounded(member.id, memberRole),
                    },
                  ]}
                  bottomRightActionContent={
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<HealingIcon />}
                      onClick={() => toggleMemberBadlyWounded(member.id, memberRole)}
                    >
                      Recover
                    </Button>
                  }
                />
              ) : (
                <WarbandMemberCard
                  key={member.id}
                  member={member}
                  isDefeated={true}
                  onItemClick={handleItemClick}
                  editMode={true}
                  titleMenuItems={[
                    {
                      label: 'Heal Bad Wound',
                      onClick: () => toggleMemberBadlyWounded(member.id, memberRole),
                    },
                  ]}
                  bottomRightActionContent={
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<HealingIcon />}
                      onClick={() => toggleMemberBadlyWounded(member.id, memberRole)}
                    >
                      Recover
                    </Button>
                  }
                />
              );
            })}
          </Box>
        </Box>
      )}

      {/* Stored in Inn */}
      {innMembers.length > 0 && (
        <Box sx={createIceSurface(42, { icicles: true, card: true })} p={1.5}>
          <SectionHeading label="Stored in Inn" />
          <Box sx={isMinified ? miniFlexSx : gridFlexSx}>
            {innMembers.map((member) => {
              const memberRole = member.creatureId.toLowerCase() === 'wizard' ? 'wizard'
                : member.creatureId.toLowerCase() === 'apprentice' ? 'apprentice'
                  : 'soldier';
              return isMinified ? (
                <WarbandMemberMiniCard
                  key={member.id}
                  member={member}
                  isDefeated={member.currentHealth <= 0}
                  onItemClick={handleItemClick}
                  editMode={true}
                  titleMenuItems={[{
                    label: 'Remove from Inn',
                    onClick: () => updateMemberStoredInInn(member.id, memberRole, 0),
                  }]}
                />
              ) : (
                <WarbandMemberCard
                  key={member.id}
                  member={member}
                  isDefeated={member.currentHealth <= 0}
                  onItemClick={handleItemClick}
                  editMode={true}
                  titleMenuItems={[{
                    label: 'Remove from Inn',
                    onClick: () => updateMemberStoredInInn(member.id, memberRole, 0),
                  }]}
                />
              );
            })}
          </Box>
        </Box>
      )}

      <ConfirmActionDialog
        open={isRemoveConfirmOpen}
        title="Confirm Removal"
        message={`Delete ${pendingRemoveSoldier?.name ?? 'this soldier'} from this party?`}
        confirmLabel="Remove"
        confirmColor="error"
        busy={deletingSoldierId !== null}
        onClose={() => {
          if (deletingSoldierId) return;
          setIsRemoveConfirmOpen(false);
          setPendingRemoveSoldier(null);
          setRefundSoldierCost(false);
          setRetainSoldierVaultItems(false);
        }}
        onConfirm={handleConfirmRemoveSoldier}
      >
        {pendingRemoveSoldier && (pendingRemoveSoldier.costPaid ?? pendingRemoveSoldier.cost) > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={refundSoldierCost}
                onChange={(e) => setRefundSoldierCost(e.target.checked)}
                size="small"
                disabled={deletingSoldierId !== null}
              />
            }
            label={<Typography variant="body2">Refund {pendingRemoveSoldier.costPaid ?? pendingRemoveSoldier.cost}gc to warband</Typography>}
            sx={{ mt: 1 }}
          />
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={retainSoldierVaultItems}
              onChange={(e) => setRetainSoldierVaultItems(e.target.checked)}
              size="small"
              disabled={deletingSoldierId !== null}
            />
          }
          label={<Typography variant="body2">Keep equipment in vault</Typography>}
          sx={{ mt: 1 }}
        />
      </ConfirmActionDialog>

      <ConfirmActionDialog
        open={isRemoveApprenticeConfirmOpen}
        title="Confirm Removal"
        message={`Remove ${apprenticeData ? apprenticeData.name : 'the apprentice'} from this party?`}
        confirmLabel="Remove"
        confirmColor="error"
        busy={isDeletingApprentice}
        onClose={() => {
          if (isDeletingApprentice) return;
          setIsRemoveApprenticeConfirmOpen(false);
          setRetainApprenticeVaultItems(false);
        }}
        onConfirm={handleConfirmRemoveApprentice}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={retainApprenticeVaultItems}
              onChange={(e) => setRetainApprenticeVaultItems(e.target.checked)}
              size="small"
              disabled={isDeletingApprentice}
            />
          }
          label={<Typography variant="body2">Keep equipment in vault</Typography>}
          sx={{ mt: 1 }}
        />
      </ConfirmActionDialog>

      <RemoveWizardModal
        open={isRemoveWizardModalOpen}
        hasApprentice={!!apprenticeData}
        busy={isDeletingWizard}
        retainVaultItems={retainWizardVaultItems}
        onRetainVaultItemsChange={setRetainWizardVaultItems}
        onClose={() => {
          if (isDeletingWizard) return;
          setIsRemoveWizardModalOpen(false);
          setRetainWizardVaultItems(false);
        }}
        onConfirmDelete={() => handleConfirmRemoveWizard(false)}
        onConfirmDeleteAndPromote={() => handleConfirmRemoveWizard(true)}
      />

      <ItemDetailsPopover popover={popover} onClose={closePopover} />
    </Box >
  );
};
